import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useAuth, UnexpectedError } from '@bowtie-web/common';
import getLogger from '../logging';
import { ALLOWED_PARENT_ORIGIN } from '../constants/index';
const logger = getLogger('auth');

const LOCAL_STORAGE_PREFIX = 'CognitoIdentityServiceProvider';
const AUTH_CALLBACK_URL_KEY = 'bowtie-quotation-auth-callback-url';
const MESSAGE_TYPES = {
  USER_DATA: 'user-data',
  QUOTATION_READY: 'quotation-ready',
  CHECK_QUOTATION_READY: 'check-quotation-ready',
} as const;

type MessageType = (typeof MESSAGE_TYPES)[keyof typeof MESSAGE_TYPES];

// Types
interface UserData {
  /** Whether the user is an existing customer */
  isExistingCustomer: boolean;
  /** URL to redirect to after authentication (for iframe scenarios) */
  authenticationCallbackUrl?: string;
}

interface AuthActions {
  /** Triggers authentication verification with cache bypass */
  triggerAuthVerification: () => void;
}

interface UserAuthContextType {
  /** Current user data */
  userData: UserData;
  /** Function to update user data */
  updateUserData: (data: UserData) => void;
  /** Authentication actions */
  authActions: AuthActions;
}

interface PostMessageData {
  message: MessageType;
  value?: {
    isExistingCustomer?: boolean;
    user?: Record<string, string> | null;
    timestamp?: number;
    origin?: string;
    authenticationCallbackUrl?: string;
  };
}

interface CognitoKeys {
  [key: string]: string;
}

const UserAuthContext = createContext<UserAuthContextType | undefined>(
  undefined,
);

export const UserAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { verify, authenticated } = useAuth();

  // Helper to get cached callback URL from localStorage
  const getCachedCallbackUrl = (): string | undefined => {
    try {
      return localStorage.getItem(AUTH_CALLBACK_URL_KEY) || undefined;
    } catch (error) {
      logger.raise(
        new UnexpectedError('Failed to get cached callback URL', error),
      );
      return undefined;
    }
  };

  // User data state - initialize with cached callback URL if available
  const [userData, setUserData] = useState<UserData>({
    isExistingCustomer: false,
    authenticationCallbackUrl: getCachedCallbackUrl(),
  });

  const updateUserData = (data: UserData) => {
    setUserData(data);
  };

  const authActions = useCallback(
    (): AuthActions => ({
      triggerAuthVerification: () => {
        verify({ bypassCache: true });
      },
    }),
    [verify],
  );

  // Memoized functions
  const sendReadySignal = useCallback(() => {
    if (window.parent && window.parent !== window) {
      try {
        const message: PostMessageData = {
          message: MESSAGE_TYPES.QUOTATION_READY,
          value: {
            timestamp: Date.now(),
            origin: window.location.origin,
          },
        };
        window.parent.postMessage(message, ALLOWED_PARENT_ORIGIN);
      } catch (error) {
        logger.raise(new UnexpectedError('Failed to send ready signal', error));
      }
    }
  }, []);

  // Helper functions for token management
  const storeTokensAndReverifyAuth = useCallback(
    (cognitoKeys: CognitoKeys) => {
      try {
        // Store only valid Cognito tokens
        Object.entries(cognitoKeys).forEach(([key, value]) => {
          if (
            key.startsWith(LOCAL_STORAGE_PREFIX) &&
            typeof value === 'string' &&
            value.trim()
          ) {
            localStorage.setItem(key, value);
          }
        });

        // Reverify with new tokens
        verify({ bypassCache: true });
      } catch (error) {
        logger.raise(new UnexpectedError('Failed to store tokens', error));
        // Still attempt to verify in case of storage issues
        verify({ bypassCache: true });
      }
    },
    [verify],
  );

  const clearAuthTokensAndReverify = useCallback(() => {
    try {
      // Remove all Cognito-related tokens from localStorage
      const keysToRemove = Object.keys(localStorage).filter(key =>
        key.startsWith(LOCAL_STORAGE_PREFIX),
      );

      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });

      // Note: We intentionally keep AUTH_CALLBACK_URL_KEY cached
      // so it persists through page reloads during logout flows

      // Reverify after clearing tokens
      verify({ bypassCache: true });
    } catch (error) {
      logger.raise(new UnexpectedError('Failed to clear tokens', error));
      // Still attempt to verify
      verify({ bypassCache: true });
    }
  }, [verify]);

  const handleUserDataMessage = useCallback(
    (
      isExistingCustomer: boolean | undefined,
      cognitoKeys: Record<string, string> | null | undefined,
      authenticationCallbackUrl: string | undefined,
    ) => {
      // Cache callback URL to localStorage if provided
      if (authenticationCallbackUrl) {
        try {
          localStorage.setItem(
            AUTH_CALLBACK_URL_KEY,
            authenticationCallbackUrl,
          );
        } catch (error) {
          logger.raise(
            new UnexpectedError('Failed to cache callback URL', error),
          );
        }
      }

      // Update user data state
      setUserData({
        isExistingCustomer: Boolean(isExistingCustomer),
        authenticationCallbackUrl,
      });

      // Handle authentication tokens
      if (
        cognitoKeys &&
        typeof cognitoKeys === 'object' &&
        Object.keys(cognitoKeys).length > 0
      ) {
        // Always store new tokens and reverify - could be different user
        storeTokensAndReverifyAuth(cognitoKeys);
      } else {
        // No cognito keys or empty object means user is not logged in
        // Clean up any stale auth data for all users
        clearAuthTokensAndReverify();
        if (authenticated) {
          // If user was authenticated but now should be logged out, reload page to reset all states
          window.location.reload();
        }
      }
    },
    [authenticated, storeTokensAndReverifyAuth, clearAuthTokensAndReverify],
  );

  const handlePostMessage = useCallback(
    (event: MessageEvent<PostMessageData>) => {
      // Validate origin
      if (ALLOWED_PARENT_ORIGIN !== event.origin) {
        return; // Invalid origin, ignore message
      }

      const { message, value } = event.data || {};

      if (!message) return;

      switch (message) {
        case MESSAGE_TYPES.USER_DATA: {
          const {
            isExistingCustomer,
            user: cognitoKeys,
            authenticationCallbackUrl,
          } = value || {};
          handleUserDataMessage(
            isExistingCustomer,
            cognitoKeys,
            authenticationCallbackUrl,
          );
          break;
        }
        case MESSAGE_TYPES.CHECK_QUOTATION_READY: {
          sendReadySignal();
          break;
        }
        default:
          // Ignore unknown message types
          break;
      }
    },
    [handleUserDataMessage, sendReadySignal],
  );

  // Handle postMessage events and cross-iframe communication
  useEffect(() => {
    window.addEventListener('message', handlePostMessage);

    // Send initial ready signal
    sendReadySignal();

    return () => {
      window.removeEventListener('message', handlePostMessage);
    };
  }, [handlePostMessage, sendReadySignal]);

  const contextValue = {
    userData,
    updateUserData,
    authActions: authActions(),
  };

  return (
    <UserAuthContext.Provider value={contextValue}>
      {children}
    </UserAuthContext.Provider>
  );
};

export const useUserData = (): UserData => {
  const context = useContext(UserAuthContext);
  if (context === undefined) {
    throw new Error('useUserData must be used within a UserAuthProvider');
  }
  return context.userData;
};

export const useAuthActions = (): AuthActions => {
  const context = useContext(UserAuthContext);
  if (context === undefined) {
    throw new Error('useAuthActions must be used within a UserAuthProvider');
  }
  return context.authActions;
};
